import type { TKey } from './types';

const zh: Record<TKey, string> = {
  'nav.library':            '书库',
  'nav.myBooks':            '我的书',
  'nav.credentials':        '证书',
  'nav.mindmap':            '思维导图',
  'nav.reading':            '阅读',
  'nav.upload':             'Upload',
  'nav.signin':             'Sign in',
  'nav.signout':            'Sign out',
  'nav.localeSelect':       'Language',

  'brand.sub':              '书任务',
  'onboard.welcome':        'Start with one book.',
  'onboard.welcomeSub':     'Pick a curated title or upload your own PDF — receive a four-layer mindmap, nine-tier adaptive quiz, and lifelong credentials.',
  'onboard.featureMapTitle':  'Four-layer mindmap',
  'onboard.featureMapBody':   'From surface narrative (L1) to the unsaid premises (L4) — a knowledge map drawn across four layers by AI.',
  'onboard.featureQuizTitle': 'Nine-tier adaptive quiz',
  'onboard.featureQuizBody':  'IRT-based difficulty (T1–T9) adjusts to your understanding in real time.',
  'onboard.featureCredTitle': 'Lifelong credentials',
  'onboard.featureCredBody':  'Every completion is issued as a W3C Verifiable Credential — externally verifiable, kept for life.',
  'onboard.ctaBrowse':      'Browse curated books',
  'onboard.ctaUpload':      'Upload my PDF',
  'onboard.signedOutHint':  'Sign in first to save your progress.',
  'onboard.ctaSignIn':      'Sign in / Sign up',
  'common.loading':         '加载中…',
  'common.offline':         '离线',
  'common.free':            '免费',
  'common.error':           '错误',
  'common.back':            '← 书库',
  'common.continue':        '继续',
  'common.submit':          '提交',
  'common.skip':            '跳过',
  'common.next':            '下一题',
  'common.finish':          '完成',
  'common.startAssessment': '仅开始评估',
  'theme.label':            '主题',
  'theme.light':            '浅色',
  'theme.dark':             '深色',
  'theme.system':           '系统',
  'sound.label':            '音效',
  'sound.on':               '开',
  'sound.off':              '关',
  'api.error.badRequest':   '请求无效。',
  'api.error.unauthorized': '请先登录。',
  'api.error.forbidden':    '您无权访问此资源。',
  'api.error.notFound':     '未找到 — 资源可能已移动或删除。',
  'api.error.tooLarge':     '文件过大。请尝试 100 MB 以内的文件。',
  'api.error.unsupported':  '不支持的文件格式。',
  'api.error.rateLimited':  '请求过多 — 稍后重试。',
  'api.error.server':       '服务器错误 — 请稍后重试。',
  'api.error.network':      '网络连接中断。',
  'api.error.unknown':      '未知错误',
  'a11y.skipToMain':        '跳到主要内容',
  'notFound.kicker':        'Not Found · 404',
  'notFound.titleLine1':    '此路径',
  'notFound.titleLine2':    '不在书架上。',
  'notFound.lead':          '书已移动,或此页从未存在。请回到图书馆重新开始。',
  'notFound.pathLabel':     '请求路径',
  'notFound.cta':           '回到图书馆',
  'footer.tagline':         '一本书化为思维导图与认证的图书馆',
  'footer.navLabel':        '页脚导航',
  'footer.about':           '关于',
  'footer.privacy':         '隐私',
  'footer.terms':           '条款',
  'footer.contact':         '联系',
  'reader.controls':              '阅读控件',
  'reader.size':                  '字号',
  'reader.theme':                 '主题',
  'reader.previewFallback':       '正文预览尚未就绪。书籍分析完成后即可显示。',
  'reader.stubNotice':            '阅读环境预览。完整正文渲染将于下个版本启用。',
  'reader.demoBody1':             '阅读时,关键概念、反驳、定义和行动项以颜色标记。',
  'reader.demoBody2':             '术语就地定义,',
  'reader.demoBody3':             '读者应尝试的是',
  'reader.demoHighlightYellow':   '关键概念',
  'reader.demoHighlightCoral':    '对立观点',
  'reader.demoHighlightBlue':     '定义',
  'reader.demoHighlightMint':     '行动项',
  'reader.aside.legendLabel':     '颜色图例',
  'reader.aside.legendYellow':    '关键概念',
  'reader.aside.legendCoral':     '反驳·对立',
  'reader.aside.legendBlue':      '定义',
  'reader.aside.legendMint':      '行动项',
  'reader.aside.metaLabel':       '书籍',
  'common.correct':         '回答正确。',
  'common.notQuite':        '差一点。',
  'common.save':            '稍后阅读',
  'common.beginReading':    '开始阅读',
  'common.openMindmap':     '打开思维导图',
  'common.searchPlaceholder': '按书名、作者或 ISBN 搜索…',

  'library.kicker':         '开馆典藏 · 100 卷',
  'library.heroLine1':      '从 100 本起步,',
  'library.heroLine2':      '书架将无止境地生长。',
  'library.heroSub':        '开馆典藏 100 选 — 每一本书都化为四层思维导图与九级理解力评估的图书馆。',
  'library.featured':       '本月推荐',
  'library.curatedCount':   '精选',
  'library.noBooks':        '尚无精选书籍。请使用 seed 脚本或管理后台。',
  'library.filterAll':      '全部',
  'library.filterClassics': '经典',
  'library.filterPhilosophy': '哲学',
  'library.filterK5':       '小学 (K-5)',
  'library.filter68':       '初中 (6-8)',
  'library.filter912':      '高中 (9-12)',

  'detail.previewNodes':    '四层思维导图预览',
  'detail.sampledQuestions': '三道样题',
  'detail.bookMissing':     '无法获取书籍信息。',
  'detail.noQuestions':     '题目尚未准备好 — 流程可能仍在处理中。',
  'detail.byAuthor':        '作者',
  'detail.sourceLabel':            '出处',
  'detail.firstQuoteLabel':        '首段引用',
  'detail.relatedCredentialLabel': '相关认证',
  'detail.relatedCredentialBody':  '完成本书可获得 Major 级认证。',
  'detail.pages':           '页',
  'detail.metaSeparator':   ' · ',

  'map.fourLayers':         '四层',
  'map.navigation':         '导航',
  'map.surface':            'L1 表层',
  'map.argument':           'L2 论证',
  'map.concept':            'L3 概念',
  'map.meta':               'L4 元层 — 未言之前提',
  'map.notReady':           '思维导图尚未就绪。',
  'map.loadingMap':         '正在加载思维导图…',
  'map.selectNode':         '请选择一个节点',
  'map.selectNodeSub':      '每个节点承载引用 (L1)、论证 (L2)、概念 (L3) 或沉默的前提 (L4)。',
  'map.hoverHint':          '将光标悬停在节点上',

  'quiz.of':                ' / ',
  'quiz.minRemaining':      '分钟剩余',
  'quiz.noQuestions':       '暂无可用题目。',
  'quiz.tier':              '难度',
  'quiz.celebration.kicker':         '完成',
  'quiz.celebration.titleLine1':     '一本书',
  'quiz.celebration.titleLine2':     '已读完。',
  'quiz.celebration.lead':           '评估已封存为认证 — 终身保管。',
  'quiz.celebration.correct':        '正确',
  'quiz.celebration.theta':          'θ 最终',
  'quiz.celebration.viewCredential': '查看认证',

  'creds.kicker':           '已颁发',
  'creds.heroLine':         '你所',
  'creds.heroEm':           '赢得的',
  'creds.heroSub':          '每一项里程碑均以 W3C Verifiable Credentials 颁发 — 可外部验证,终身保存。',
  'creds.master':           'Master ★',
  'creds.recent':           '最近颁发',
  'creds.pastDays':         '近 30 天',
  'creds.noneYet':          '完成一本书以获得首份证书。',
  'creds.urn':              'URN',
  'creds.issued':           '颁发',
  'creds.anchor':           '锚定',
  'creds.tier.micro':       '微章',
  'creds.tier.standard':    '标准',
  'creds.tier.major':       '主章',
  'creds.tier.master':      '大师',
  'creds.tier.legendary':   '传奇',
  'creds.count':            '张',
  'creds.aside.tiersLabel':    '5-Tier 阶梯',
  'creds.aside.tiersBody':     'Micro → Standard → Major → Master → Legendary。一生累计 80–100 张。',
  'creds.aside.verifyLabel':   '外部验证',
  'creds.aside.verifyBody':    'URN 遵循 W3C Verifiable Credentials 标准 — 任何合规验证方均可核验。',
  'creds.aside.standardLabel': '标准',
  'creds.justIssued':          '刚刚颁发',
  'creds.copied':              '已复制',
  'creds.copyFailed':          '复制失败',
  'creds.share':               '分享',
  'creds.verifyExternally':    '外部验证',
  'creds.verifierComing':      '验证门户即将上线。',
  'creds.shareNative':         '分享',
  'creds.print':               '打印·PDF',
  'source.curated':         '精选',
  'source.user_upload':     '用户上传',
  'source.partner':         '合作伙伴',
  'source.public_domain':   '公有领域',
  'source.institution':     '机构',
  'auth.signinTitle':       'Sign in',
  'auth.signupTitle':       'Sign up',
  'auth.magicTitle':        'Magic link',
  'auth.email':             'Email',
  'auth.password':          'Password',
  'auth.signin':            'Sign in',
  'auth.signup':            'Create account',
  'auth.sendMagic':         'Send magic link',
  'auth.switchToSignup':    'New here? Create account',
  'auth.switchToSignin':    'Already have an account? Sign in',
  'auth.switchToMagic':     'Use magic link instead',
  'auth.notConfiguredBanner': 'Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in packages/web-app/.env.local.',
  'auth.checkEmail':        'Check your email to confirm your account.',
  'auth.magicSent':         'Magic link sent — check your email.',
  'auth.working':           'Working…',
  'auth.aside.whyLabel':       '为何选择 BookQuest',
  'auth.aside.why1':           '一本书化为 4-Layer 思维导图。',
  'auth.aside.why2':           '9-Tier 评估测量阅读理解力。',
  'auth.aside.why3':           '终身认证不断累积。',
  'auth.aside.privacyLabel':   '隐私',
  'auth.aside.privacyBody':    '阅读数据仅本人可见。',

  'upload.title':           'Upload a book',
  'upload.help':            'PDFs only. The system de-duplicates globally — if anyone has already processed this file you get instant free access.',
  'upload.fileLabel':       'PDF file',
  'upload.countryLabel':    'Country (for pricing)',
  'upload.submit':          'Upload',
  'upload.submitting':      'Uploading…',
  'upload.chooseFirst':     'Choose a PDF first.',
  'upload.failed':          'Upload failed',
  'upload.resultTitle':     'Result',
  'upload.dedupeStatus':    'De-dup status',
  'upload.paymentStatus':   'Payment status',
  'upload.amount':          'Amount',
  'upload.jobId':           'Job ID',
  'upload.eta':             'ETA',
  'upload.watchProgress':   'Watch progress',
  'job.title':              '分析进度',
  'job.processing':         '正在分析书籍并生成题目…',
  'job.stage':              '阶段',
  'job.cost':               '累计费用',
  'job.done':               '已完成',
  'job.failed':             '处理过程中出错',
  'job.openBook':           '前往书籍',
  'job.eta':                '通常约20分钟。关闭此页也会在后台继续。',
  'upload.aside.formatLabel':  '支持格式',
  'upload.aside.formatBody':   '可提取文本的 PDF。50 MB 以内。',
  'upload.aside.etaLabel':     '预计处理时间',
  'upload.aside.etaBody':      '5–15 分钟,取决于书籍长度与队列。',
  'upload.aside.privacyLabel': '隐私',
  'upload.aside.privacyBody':  '上传的 PDF 在分析后立即删除。',
  'nav.achievements':       '成就',
  'nav.admin':              '管理',
  'gam.kicker':             '你的旅程',
  'gam.heroLine':           '阅读、解题、',
  'gam.heroEm':             '成长',
  'gam.heroSub':            '每答对一题、读完一本书都会获得XP和徽章。提升等级，在排行榜上留下你的名字。',
  'gam.levelLabel':         '等级',
  'gam.xpTotal':            '累计XP',
  'gam.xpUnit':             'XP',
  'gam.toNext':             '距下一级',
  'gam.maxLevel':           '最高等级',
  'gam.badges':             '徽章',
  'gam.badgesSub':          '学习的印记',
  'gam.locked':             '未获得',
  'gam.noBadges':           '还没有徽章 — 从第一题开始吧。',
  'gam.leaderboard':        '排行榜',
  'gam.leaderboardSub':     'XP排名',
  'gam.rank':               '排名',
  'gam.you':                '我',
  'gam.emptyBoard':         '暂无排名。',
  'gam.period.daily':       '今天',
  'gam.period.weekly':      '本周',
  'gam.period.monthly':     '本月',
  'gam.period.all':         '全部',
  'gam.howXp':              '如何获得XP',
  'gam.howXpSub':           'XP就是这样累积的',
  'gam.levelCurve':         '等级曲线',
  'gam.levelN':             'Lv',
  'gam.aside.label':        '什么是XP？',
  'gam.aside.body':         'XP是你学习活动的累计记录。答对、完成阶段、读完书都会累加成等级。',
  'gam.aside.fairLabel':    '公平累积',
  'gam.aside.fairBody':     '重复回答同一题，XP只在首次答对时计入一次。',
  'gam.xpRule.answer_correct':     '答对题目',
  'gam.xpRule.stage_complete':     '完成阶段',
  'gam.xpRule.book_clear':         '读完书籍',
  'gam.xpRule.cr_submit':          '提交简答',
  'gam.xpRule.streak_day':         '连续学习(1天)',
  'gam.xpRule.question_propose':   '提议题目',
  'gam.xpRule.question_adopted':   '提议被采纳',
  'gam.xpRule.review_participate': '参与评审',
  'detail.propose':            '提议题目',
  'prop.title':                '提议一道题',
  'prop.help':                 '为这本书编写一道选择题并提议。管理员采纳后会加入题库，你将获得XP和徽章。',
  'prop.stemLabel':            '题目（题干）',
  'prop.stemPlaceholder':      '例如：本章中主人公离开的原因，最恰当的是？',
  'prop.choicesLabel':         '选项（请选择正确答案）',
  'prop.choiceCorrect':        '正确',
  'prop.choicePlaceholder':    '选项内容',
  'prop.explanationLabel':     '解析（可选）',
  'prop.explanationPlaceholder': '请结合原文说明正确答案的依据。',
  'prop.submit':               '提交提议',
  'prop.submitting':           '提交中…',
  'prop.success':              '提议已收到。采纳后会通知你。',
  'prop.needStem':             '请输入题目内容。',
  'prop.needChoices':          '请至少填写两个选项。',
  'prop.needCorrect':          '请选择正确答案。',
  'prop.myProposals':          '我的提议',
  'prop.none':                 '还没有提议的题目。',
  'prop.status.pending':       '审核中',
  'prop.status.accepted':      '已采纳',
  'prop.status.rejected':      '未采纳',
  'prop.aside.howLabel':       '运作方式',
  'prop.aside.howBody':        '提议 → 管理员审核 → 采纳后进入题库（再经质检后出题）。题目越好越容易被采纳。',
  'prop.aside.rewardLabel':    '奖励',
  'prop.aside.rewardBody':     '提议可得XP，被采纳可得更多XP及“题目提议者”徽章。',
  'prop.difficulty':           '建议难度（可选）',
  'prop.difficultyNone':       '未指定',
  'detail.report':            '成就报告',
  'quiz.celebration.viewReport': '查看报告',
  'report.kicker':            '评估报告',
  'report.title':             '成就报告',
  'report.accuracy':          '总正确率',
  'report.level':             '熟练度',
  'report.theta':             '能力估计(θ)',
  'report.thetaNote':         '相对估计值 — 校准前的近似',
  'report.attempted':         '已答',
  'report.unit':              '题',
  'report.naep':              'NAEP 认知领域',
  'report.lidia':             'LIDIA 评估领域',
  'report.trend':             '成长趋势',
  'report.window':            '区间',
  'report.notAttempted':      '未作答',
  'report.empty':             '还没有评估记录',
  'report.emptySub':          '答题后各领域成就会显示在这里。',
  'report.startQuiz':         '开始评估',
  'report.honestyLabel':      '指标说明',
  'report.honestyBody':       '等级·θ为校准前的相对近似值，并非标准分数(Lexile·高考等级)。',
  'report.viewCredential':    '查看证书',
  'report.viewAchievements':  '成就·XP',
  'report.retry':             '重新作答',
  'report.naep.locate_recall':       '定位·记忆',
  'report.naep.integrate_interpret': '整合·解释',
  'report.naep.critique_evaluate':   '批判·评价',
  'footer.larc':              '审查委员会',
  'larc.kicker':             '信任与透明',
  'larc.title':              'LARC 审查委员会',
  'larc.lead':               '所有题目均由AI生成，再经多阶段审查。LARC 公开这一过程。',
  'larc.missionLabel':       '使命',
  'larc.missionBody':        '守护评估的公平与准确。AI生成的题目由人工和交叉模型验证，提供可信的素养测量。',
  'larc.criteriaLabel':      '审查标准',
  'larc.crit1':              '依据 — 答案是否真正基于原文',
  'larc.crit2':              '清晰 — 题干和选项是否无歧义',
  'larc.crit3':              '区分度 — 干扰项是否合理、答案是否唯一',
  'larc.crit4':              '学段·偏见 — 是否适合目标学段、无偏见',
  'larc.statsLabel':         '审查活动',
  'larc.stApproved':         '已通过',
  'larc.stPending':          '待审查',
  'larc.stReviewed':         'AI审查',
  'larc.stRejected':         '已驳回',
  'larc.stTotal':            '题目总数',
  'larc.stReports':          '举报处理',
  'larc.note':               '仅公开汇总数字，不含个人信息或题目内容。',
  'prelearn.title':          '预习',
  'prelearn.subtitle':       '阅读前的背景知识',
  'prelearn.books':          '购买·书店',
  'prelearn.media':          '视频·背景',
  'prelearn.note':           '将跳转到外部网站搜索。',
  'creds.summaryLabel':      '综合素养认证',
  'creds.summaryTotal':      '总认证',
  'creds.summaryBooks':      '覆盖书籍',
  'creds.summaryHighest':    '最高等级',
  'nav.vault':               '知识库',
  'vault.title':             '知识库',
  'vault.subtitle':          '收集笔记·金句·洞见',
  'vault.kindMemo':          '笔记',
  'vault.kindQuote':         '金句',
  'vault.kindInsight':       '洞见',
  'vault.kindFeedback':      '反馈',
  'vault.contentPlaceholder': '写下你想记住的内容',
  'vault.tagsPlaceholder':   '标签（逗号分隔）',
  'vault.add':               '保存',
  'vault.searchPlaceholder': '搜索内容',
  'vault.allKinds':          '全部',
  'vault.empty':             '还没有保存的笔记。',
  'vault.delete':            '删除',
  'vault.added':             '已保存。',
  'vault.deleted':           '已删除。',
  'vault.needContent':       '请输入内容。',
  'vault.export':            '导出',
  'vault.exportMd':          'Markdown',
  'vault.exportJson':        'JSON',
  'verify.title':            '证书验证',
  'verify.checking':         '验证中…',
  'verify.valid':            '有效证书',
  'verify.invalid':          '无效或已撤销的证书',
  'verify.notFound':         '未找到证书',
  'verify.tier':             '等级',
  'verify.issued':           '颁发日期',
  'verify.note':             '与 BookQuest 颁发记录核对验证。不公开个人信息。',
};

export default zh;
